import React from 'react';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import { ReactComponent as GustoIcon } from '../../../../assets/svg/vendors/gusto-icon.svg';
import { ReactComponent as RedWhiteError } from '../../../../assets/svg/shared/icon-red-white-error.svg';
import { getNumOfDaysLeft } from '../../../../helpers/datesAndTime.helper';
import { convertValueToUSD } from '../../../../helpers/currency.helper';
import { ReactComponent as ReloadIcon } from '../../../../assets/svg/reload.svg';
import { ReactComponent as RightArrow } from '../../../../assets/icons/svg/shared/ic-shared-right-arrow.svg';

interface ApiError {
  errorCode: number;
  title: string;
  text: string;
}

interface IGustoWidgetProps {
  paymentAmount?: number;
  paymentDate?: string;
  gustoLink?: string;
  isMockMode?: boolean;
  error?: string;
  apiError?: ApiError | undefined;
  onReconnectGusto?: any;
  isNewWidgetMode?: boolean;
}

interface IGustoLoadingErrorWidgetProps {
  title: string;
  text: string;
}

interface IGustoTokenErrorWidgetProps extends IGustoLoadingErrorWidgetProps {
  onClick: () => void;
}

const ExpiredToken = ({ title, text, onClick }: IGustoTokenErrorWidgetProps) => {
  const { t } = useTranslation();
  return (
    <div className='flex flex-col items-center justify-center h-[266px] bg-bw00'>
      <div className='text-center text-[14px] font-metricR text-bw08'>
        <h1 className='font-metricRMedium text-md'>{title}</h1>
        <p>{text}</p>
      </div>

      <button
        type='button'
        onClick={onClick || (() => {})}
        className='mt-8 px-[14px] py-[6px] bg-action03 text-bw00 text-[12px] font-metricR'
      >
        {t('GUSTO.WIDGET.RECONNECT_BUTTON')}
      </button>
    </div>
  );
};

const LoadingError = ({ title, text }: IGustoLoadingErrorWidgetProps) => (
  <div className='flex flex-col items-center justify-center h-[266px] bg-bw00'>
    <ReloadIcon />
    <div className='text-center text-[14px] font-metricR text-bw08'>
      <h1 className='font-metricRMedium text-md'>{title}</h1>
      <p>{text}</p>
    </div>
  </div>
);

const GustoWidget = ({
  paymentAmount = 3090,
  paymentDate = 'January 1, 2024',
  gustoLink = '',
  isMockMode,
  error,
  apiError,
  onReconnectGusto,
  isNewWidgetMode,
}: IGustoWidgetProps) => {
  const { t } = useTranslation();
  const formattedPaymentDate = format(new Date(paymentDate), 'MMMM dd, yyyy');
  const calculateRemainingDaysLeft = getNumOfDaysLeft(paymentDate);
  const calcStr =
    calculateRemainingDaysLeft === 0
      ? 'Today'
      : `${calculateRemainingDaysLeft} ${calculateRemainingDaysLeft > 1 ? 'days' : 'day'}`;

  const getWidgetBody = () => {
    if (!apiError) {
      return (
        <>
          <div
            className={`border-b-[0.7px] border-bw05/[.3] ${isMockMode ? 'px-[16px] pt-[12px] pb-[16px] ' : 'px-[23px] pt-[18px] pb-[19px]'}`}
          >
            <div className={`flex justify-between ${isMockMode ? 'mb-[15px]' : error ? 'mb-[11px]' : 'mb-[35px]'}`}>
              <div className={`font-metricR text-bw06 ${isMockMode ? 'text-[11px]' : 'text-md leading-6'}`}>
                {t('GUSTO.WIDGET.PAYMENT_AMOUNT')}
              </div>
              <div className='text-right'>
                <div className={`font-metricRMedium text-bw08 ${isMockMode ? 'text-[11px]' : 'text-md leading-6'}`}>
                  {convertValueToUSD(paymentAmount, 2)}
                </div>
                {error && (
                  <div className='font-metricR text-red02 text-sm flex items-center'>
                    <RedWhiteError className='w-[10px] h-[10px] mr-[3px] relative top-[-1px]' />
                    {error}
                  </div>
                )}
              </div>
            </div>
            <div className='flex justify-between'>
              <div className={`font-metricR text-bw06 ${isMockMode ? 'text-[11px]' : 'text-md leading-6'}`}>
                {t('GUSTO.WIDGET.PAYMENT_DATE')}
              </div>
              <div className='text-right'>
                <div className={`font-metricRMedium text-bw08 ${isMockMode ? 'text-[11px]' : 'text-md leading-6'}`}>
                  {formattedPaymentDate}
                </div>
                <div className={`font-metricR text-bw06 ${isMockMode ? 'text-[9.5px]' : 'text-sm'}`}>
                  {calculateRemainingDaysLeft === 0 ? '' : 'In'}
                  <span className='ml-[3px] font-metricRSemibold'>{calcStr}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Widget footer */}
          <div className={`flex items-center justify-center ${isMockMode ? 'h-[37px]' : 'h-[55px]'}`}>
            <div
              data-testid='GustoWidget__link'
              onClick={() => gustoLink && window.open(gustoLink, '_blank')}
              role='button'
              onKeyDown={() => {}}
              tabIndex={0}
              className='flex items-center justify-center hover:cursor-pointer'
            >
              <div className={`font-metricRMedium text-action03New ${isMockMode ? 'text-[9.5px]' : 'text-sm'}`}>
                {t('GUSTO.WIDGET.LINK_TEXT')}
              </div>
            </div>
            <RightArrow className='w-[14px] h-[14px] text-action03' />
          </div>
        </>
      );
    }

    // Code 50692 is for expired token
    if (apiError?.errorCode === 50692) {
      return <ExpiredToken title={apiError.title} text={apiError.text} onClick={onReconnectGusto} />;
    }

    // Code 50693 is for loading error
    if (apiError?.errorCode === 50693) {
      return <LoadingError title={apiError.title} text={apiError.text} />;
    }

    return null;
  };

  return (
    <div
      className={`bg-bw00 flex flex-col ${isMockMode ? 'w-[256px] h-[179px]' : 'w-full h-[266px] mb-[1rem]'} ${isNewWidgetMode ? 'rounded-[12px]' : ''}`}
    >
      <div
        className={`flex items-center justify-between px-[16px] border-b-[0.7px] border-bw05/[.3] ${isMockMode ? 'h-[43px]' : 'h-[63px]'}`}
      >
        <h1 className={`font-metricRMedium text-bw08 ${isMockMode ? 'text-[11px]' : 'text-md'}`}>
          {t('GUSTO.WIDGET.TITLE')}
        </h1>
        <GustoIcon className={`rounded-[6px] ${isMockMode ? 'w-[32px] h-[32px]' : 'w-[42px] h-[42px]'}`} />
      </div>

      {getWidgetBody()}
    </div>
  );
};

export default GustoWidget;
